import { unionPolygons } from "../unionPolygons";
import type { SelectionAreaPoint } from "../../types";
import type { MultiPolygon, PolygonPiece, PolygonRing } from "../types";
import type { BridgePolygonPieces } from "./types";

// Every bridge joins two pieces, so there are never more bridges than pieces.
const MAX_BRIDGES = 50;

interface ClosestPoints {
  distance: number;
  from: SelectionAreaPoint;
  to: SelectionAreaPoint;
}

const closestPointOnSegment = (
  point: SelectionAreaPoint,
  start: SelectionAreaPoint,
  end: SelectionAreaPoint,
): SelectionAreaPoint => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) return start;
  const projection =
    ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSquared;
  const t = Math.max(0, Math.min(1, projection));
  return { x: start.x + t * dx, y: start.y + t * dy };
};

// The shortest line from a vertex of one ring to an edge of the other, checked both ways.
const findClosestPoints = (
  ringA: PolygonRing,
  ringB: PolygonRing,
): ClosestPoints => {
  let closest: ClosestPoints = {
    distance: Infinity,
    from: ringA[0],
    to: ringB[0],
  };
  const check = (
    vertices: PolygonRing,
    edges: PolygonRing,
    isSwapped: boolean,
  ) => {
    for (const vertex of vertices) {
      for (let index = 0; index < edges.length; index++) {
        const foot = closestPointOnSegment(
          vertex,
          edges[index],
          edges[(index + 1) % edges.length],
        );
        const distance = Math.hypot(vertex.x - foot.x, vertex.y - foot.y);
        if (distance < closest.distance) {
          closest = isSwapped
            ? { distance, from: foot, to: vertex }
            : { distance, from: vertex, to: foot };
        }
      }
    }
  };
  check(ringA, ringB, false);
  check(ringB, ringA, true);
  return closest;
};

// The axis-aligned box around two points, widened by half the strip so it is never thinner than a wall.
// NOTE: the box also reaches half a wall past both endpoints; trimming it to the endpoints is a possible follow-up.
const buildStrip = (
  start: SelectionAreaPoint,
  end: SelectionAreaPoint,
  width: number,
): PolygonPiece => {
  const half = width / 2;
  const minX = Math.min(start.x, end.x) - half;
  const maxX = Math.max(start.x, end.x) + half;
  const minY = Math.min(start.y, end.y) - half;
  const maxY = Math.max(start.y, end.y) + half;
  return [
    [
      { x: minX, y: minY },
      { x: maxX, y: minY },
      { x: maxX, y: maxY },
      { x: minX, y: maxY },
    ],
  ];
};

// Walls run along the axes, so the bridge does too: a horizontal strip and a vertical one meeting at an elbow.
const buildBridge = (
  { from, to }: ClosestPoints,
  width: number,
): PolygonPiece[] => {
  const elbow = { x: to.x, y: from.y };
  return [buildStrip(from, elbow, width), buildStrip(elbow, to, width)];
};

// Joins the two closest pieces with a strip, until one piece is left.
export const bridgePolygonPieces: BridgePolygonPieces = ({ pieces, width }) => {
  let joined: MultiPolygon = pieces;
  for (let bridges = 0; joined.length > 1 && bridges < MAX_BRIDGES; bridges++) {
    let closest: ClosestPoints | null = null;
    for (let first = 0; first < joined.length; first++) {
      for (let second = first + 1; second < joined.length; second++) {
        const candidate = findClosestPoints(
          joined[first][0],
          joined[second][0],
        );
        if (closest === null || candidate.distance < closest.distance) {
          closest = candidate;
        }
      }
    }
    if (closest === null) break;
    joined = unionPolygons({
      pieces: [...joined, ...buildBridge(closest, width)],
    });
  }
  return joined;
};
